import { model as i18n } from '!/i18n'
import { LiveEventsTypes } from '@setplex/tria-api'
import { combine, createEvent, createStore, split, type Store } from 'effector'
import { and } from 'patronum'
import { options } from '~/core/flags'
import {
  formatLiveEventWithPrice,
  formatPastEventWithPrice,
  model as formattedEventsEntityModel,
} from '~/entities/live-events/events'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'

type LiveEventsTab = {
  id: LiveEventsTypes
  page?: number
  count?: number
}

export const changeActiveTab = createEvent<LiveEventsTab>()
export const redirectToTab = createEvent<LiveEventsTypes>()
export const loadMoreEvents = createEvent<LiveEventsTab>()

export const $activeTab = createStore<LiveEventsTypes | null>(null).on(
  changeActiveTab,
  (_, { id }) => id
)
export const $redirectTab = createStore<LiveEventsTypes>(
  LiveEventsTypes.LIVE
).on(redirectToTab, (_, payload) => payload)

// TODO: perhaps place $pastEventsEnabled in more suitable layer where we can reuse it in all upper layers
export const $pastEventsEnabled = options.$liveEventRecordsEnabled

export const $isAnyEvents = combine(
  [
    formattedEventsEntityModel.$liveEvents,
    formattedEventsEntityModel.$upcomingEvents,
    formattedEventsEntityModel.$pastEvents,
  ],
  (events) => events?.some((item) => item?.length)
)

/***** hack to show/hide tabs wrapper with all tabs inside *****/

// TODO: maybe better to move type and store below in separated file
type EventsTabNames = {
  live: string
  upcoming: string
  past: string
}

export const $eventsTabNames: Store<EventsTabNames> = combine(
  i18n.$t,
  ({ t }) => ({
    live: t('live_now'),
    upcoming: t('upcoming'),
    past: t('events_past'),
  })
)

export const $isPastEventsTabShown = and(
  $pastEventsEnabled,
  formattedEventsEntityModel.$pastEvents.map((content) => content?.length)
)

export const $allEvents = combine(
  {
    live: formattedEventsEntityModel.$liveEvents,
    upcoming: formattedEventsEntityModel.$upcomingEvents,
    past: formattedEventsEntityModel.$pastEvents,
    pastEventsEnabled: options.$liveEventRecordsEnabled,
    name: $eventsTabNames,
    activeTab: $activeTab,
    adjust: ssePurchasesModel.$liveEvent,
  },
  ({ live, upcoming, past, name, activeTab, pastEventsEnabled, adjust }) => [
    {
      contentType: LiveEventsTypes.LIVE,
      title: name.live,
      list: live.map((event) => formatLiveEventWithPrice(adjust(event))),
      selected: LiveEventsTypes.LIVE === activeTab,
      hasContent: Boolean(live?.length),
    },
    {
      contentType: LiveEventsTypes.UPCOMING,
      title: name.upcoming,
      list: upcoming.map((event) => formatLiveEventWithPrice(adjust(event))),
      selected: LiveEventsTypes.UPCOMING === activeTab,
      hasContent: Boolean(upcoming?.length),
    },
    {
      contentType: LiveEventsTypes.PAST,
      title: name.past,
      list: past.map((event) => formatPastEventWithPrice(adjust(event))),
      selected: LiveEventsTypes.PAST === activeTab,
      hasContent: Boolean(past?.length) && pastEventsEnabled,
    },
  ]
)

split({
  source: loadMoreEvents,
  match: $activeTab,
  cases: {
    [LiveEventsTypes.LIVE]: formattedEventsEntityModel.loadMoreLiveEvents,
    [LiveEventsTypes.UPCOMING]:
      formattedEventsEntityModel.loadMoreUpcomingEvents,
    [LiveEventsTypes.PAST]: formattedEventsEntityModel.loadMorePastEvents,
  },
})
